import { CommonModule, DatePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifyService } from '@neutrino/core/notify';
import { DIALOG_DIRECTIVES, DialogComponent, DialogEvent } from '@neutrino/core/dialog';
import { INPUT_DIRECTIVES } from '@neutrino/core/inputs';
import { codeBlock } from 'common-tags';
import {
    BehaviorSubject,
    combineLatest,
    delay,
    filter,
    firstValueFrom,
    map,
    merge,
    Observable,
    ReplaySubject,
    Subject,
    switchMap
} from 'rxjs';
import { MarkdownPipe } from 'ngx-markdown';
import { stringify as YamlStringify } from 'yaml';
import logger from 'loglevel';

import {
    SupportRequestComment,
    SupportRequest,
    SupportRequestStatus,
    SupportRequestCategory,
    SupportRequestClientInfo
} from '@flb/common/support-request';
import { assertIsError, assertNotNullish, truthy } from '@flb/common/util';
import { fullClientRoutes } from '@flb/common/client-routes';
import { AccessLevel } from '@flb/common/access-level';
import { evalPathExpr } from '@flb/common/eval-path-expr';

import { MdeComponent } from '../mde/mde.component.js';
import { ClientStateService } from '../common/client-state.service.js';
import { parseParams, setRouteFragment } from '../common/url-params.js';
import { SlideChild } from '@neutrino/core/slide';
import { SupportRequestNotificationService } from './support-request-notification.service.js';

export enum ParamKey {
    RequestId = 'requestId',
    AddComment = 'addComment'
}
export class SupportRequestParams {
    /**
     * Parameters for the error report form.
     * @param id ID of the error report to display, or null to create a new one.
     */
    constructor(public readonly id: string | null) {}
}

// {{expr}} will be replaced with <!-- please enter a value -->, {{expr|}} will not
const DEFAULT_DESCRIPTION = codeBlock`
    # Issue Description

    <!--- Describe your issue here. Attach screenshots and/or video of the issue if possible. --->

    # Team Info

    Team name: {{teamName}}
    Player: {{userInfo.name}}

    {{userInfo|}}
    {{clientTrackingState|}}
    # System Details
    {{deviceInfoReport}}

    <!--- copy from Options -> System Details at the connect screen --->
`;

@Component({
    selector: 'flb-support-request',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ...DIALOG_DIRECTIVES,
        ...INPUT_DIRECTIVES,
        MdeComponent,
        MarkdownPipe,
        DatePipe
    ],
    template: `
        <nu-dialog
            *ngIf="supportRequest"
            [dialogTitle]="dialogTitle"
            [form]="form"
            [loading]="!supportRequest || (saving | async) === true"
            (dialogSubmitting)="submit($event)"
            #dialog
        >
            <form #form="ngForm">
                <nu-dialog-section>
                    <ng-template nuDialogSectionContent>
                        <label [nuFor]="titleInput">Title</label>
                        <nu-field>
                            <input
                                type="text"
                                class="wide"
                                name="title"
                                required
                                [(ngModel)]="supportRequest.title"
                                #titleInput
                            />
                        </nu-field>
                        @if (supportRequest.id) {
                            <label [nuFor]="statusSelect">Status</label>
                            <nu-field>
                                <select
                                    name="status"
                                    [(ngModel)]="supportRequest.status"
                                    #statusSelect
                                >
                                    <option
                                        *ngFor="let status of supportRequestStatuses"
                                        [value]="status"
                                    >
                                        {{ status }}
                                    </option>
                                </select>
                            </nu-field>
                        }
                        <label [nuFor]="categorySelect">Category</label>
                        <nu-field>
                            <select
                                name="category"
                                [(ngModel)]="supportRequest.category"
                                #categorySelect
                            >
                                <option
                                    *ngFor="let category of supportRequestCategories"
                                    [value]="category"
                                >
                                    {{ category }}
                                </option>
                            </select>
                        </nu-field>
                        <label [nuFor]="emailInput">Email</label>
                        <nu-field>
                            <input
                                type="email"
                                class="wide"
                                name="email"
                                [(ngModel)]="supportRequest.email"
                                [disabled]="!!supportRequest.id || (admin | async) !== true"
                                required
                                #emailInput
                            />
                        </nu-field>
                        <label [nuFor]="descriptionEditor">Description</label>
                        <nu-field>
                            <flb-mde
                                class="wide"
                                required
                                [foldSections]="foldSections"
                                [(ngModel)]="supportRequest.description"
                                name="description"
                                #descriptionEditor
                            ></flb-mde>
                        </nu-field>
                        @if (supportRequest.id) {
                            <nu-field class="sticky-bottom below-bottom">
                                <span class="wide"
                                    ><a
                                        [href]="addCommentFragment"
                                        class="right-link"
                                        (click)="scrollToAddComment($event)"
                                        >Add a comment</a
                                    ></span
                                >
                            </nu-field>
                        }
                    </ng-template>
                </nu-dialog-section>
            </form>
            <form #commentForm="ngForm">
                @if (supportRequest.id) {
                    <nu-dialog-section>
                        <ng-template nuDialogSectionLabel>
                            Conversation
                            <a
                                [href]="addCommentFragment"
                                class="right-link"
                                (click)="scrollToAddComment($event)"
                                >Add a comment</a
                            >
                        </ng-template>
                        <ng-template nuDialogSectionContent>
                            @for (comment of comments; track comment) {
                                <label>{{ comment.author }}</label>
                                <nu-field>
                                    <div class="flex-column">
                                        <span>{{ comment.created | date }}</span>
                                        <span
                                            [innerHTML]="comment.message | markdown | async"
                                        ></span>
                                    </div>
                                </nu-field>
                            }
                        </ng-template>
                    </nu-dialog-section>
                    <nu-dialog-section>
                        <ng-template nuDialogSectionLabel> Add a comment </ng-template>
                        <ng-template nuDialogSectionContent>
                            <label [nuFor]="commentEditor" #commentEditorLabel></label>
                            <nu-field>
                                <flb-mde
                                    class="wide"
                                    name="new-comment"
                                    [(ngModel)]="newComment"
                                    [disabled]="(savingComment | async) === true"
                                    #commentEditor
                                ></flb-mde>
                            </nu-field>
                            <nu-field class="single-input sticky-bottom">
                                <div class="flex-end wide margin-between">
                                    <button
                                        type="button"
                                        (click)="addComment(true)"
                                        [style.display]="newComment && !wasResolved ? '' : 'none'"
                                        [disabled]="(savingComment | async) === true"
                                    >
                                        Resolve with comment
                                    </button>
                                    <button
                                        class="primary"
                                        type="button"
                                        (click)="addComment()"
                                        [style.display]="newComment ? '' : 'none'"
                                        [disabled]="(savingComment | async) === true"
                                    >
                                        Post comment
                                    </button>
                                </div>
                            </nu-field>
                        </ng-template>
                    </nu-dialog-section>
                }
            </form>
            <footer>
                <nu-dialog-submit-button [disabled]="!form.dirty">
                    <span>{{ origSupportRequest ? 'Save changes' : 'Create' }}</span>
                </nu-dialog-submit-button>
                <nu-dialog-close-button>
                    <span>Close</span>
                </nu-dialog-close-button>
            </footer>
        </nu-dialog>
    `,
    styleUrl: './support-request.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupportRequestComponent implements OnDestroy, SlideChild<void> {
    readonly SupportRequestCategory = SupportRequestCategory;
    readonly supportRequestStatuses = Object.values(SupportRequestStatus).filter(
        s => typeof s === 'string'
    );
    readonly supportRequestCategories = Object.values(SupportRequestCategory).filter(
        s => typeof s === 'string'
    );
    readonly dialogTitle = 'Support request';
    newComment = '';
    savingComment = new BehaviorSubject(false);

    @ViewChild('form') set formSet(value: NgForm) {
        this.form.next(value);
    }

    @ViewChild('dialog') set dialogSet(value: DialogComponent) {
        this.dialog.next(value);
    }

    @ViewChild('commentEditorLabel') set commentEditorSet(value: ElementRef<HTMLElement>) {
        if (value && value.nativeElement) {
            this.commentEditorElem.next(value);
        }
    }

    form = new BehaviorSubject<NgForm | null>(null);
    dialog = new ReplaySubject<DialogComponent>(1);
    commentEditorElem = new ReplaySubject<ElementRef<HTMLElement>>(1);
    saving = new BehaviorSubject(false);

    supportRequest: SupportRequest | null = null;
    origSupportRequest: SupportRequest | null = null;
    comments: SupportRequestComment[] = [];
    foldSections: string[] = ['# Client Info', '# User Info', '# System Details'];
    wasResolved = false;
    addCommentFragment = '';
    protected admin: Observable<boolean>;
    destroyed = new Subject<void>();

    constructor(
        private clientState: ClientStateService,
        private httpClient: HttpClient,
        public params: SupportRequestParams,
        private notify: NotifyService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private changeDetectorRef: ChangeDetectorRef,
        private supportRequestNotification: SupportRequestNotificationService
    ) {
        this.admin = clientState.authToken.pipe(map(t => t?.accessLevel !== AccessLevel.Player));
        if (params.id) {
            this.getSupportRequest(params.id).catch(err => this.notifyError(err));
        } else {
            const description = DEFAULT_DESCRIPTION.replace(/{{([.A-Z_a-z]+)(|-)?}}/g, '');

            this.supportRequest = {
                id: params.id || '',
                sessionId: '',
                email: clientState.authToken.value?.email || '',
                title: '',
                description,
                created: '',
                updated: '',
                status: SupportRequestStatus.New,
                category: SupportRequestCategory.Objective
            };
            if (clientState.authToken.value?.accessLevel === AccessLevel.Player) {
                this.fillClientDetails().catch(err => this.notifyError(err));
            } else {
                this.foldSections = [];
            }
        }
        this.addCommentFragment = `#${this.activatedRoute.snapshot.fragment || ''}`;
    }

    slideCancelConfirm() {
        const id = this.getRouteFragmentParams().get(ParamKey.RequestId);
        if (id == this.params.id) {
            this.setRouteFragmentParam(ParamKey.RequestId, undefined);
        }
        return true;
    }

    ngOnDestroy(): void {
        this.destroyed.next();
    }

    setRouteFragmentParam(param: ParamKey, value: string | undefined) {
        setRouteFragment(
            this.getRouteFragmentParams(),
            this.router,
            this.activatedRoute,
            param,
            value
        );
    }

    getRouteFragmentParams() {
        return parseParams(this.activatedRoute.snapshot.fragment);
    }

    scrollToAddComment(event?: Event) {
        event?.preventDefault();
        const elemReady = combineLatest([
            this.commentEditorElem,
            this.dialog.pipe(
                filter(truthy),
                switchMap(d => d.initialized),
                filter(truthy),
                // dialog tries to scroll to the top immediately after initialization..
                // so we need to delay until after that happened
                delay(0)
            )
        ]);
        firstValueFrom(merge(elemReady, this.destroyed.pipe(map(() => []))))
            .then(([e]) => {
                if (e) {
                    e.nativeElement?.scrollIntoView({ behavior: 'smooth' });
                    if (event) {
                        this.setRouteFragmentParam(ParamKey.AddComment, '');
                    }
                }
            })
            .catch(ex => logger.error('scrollToAddComment', ex));
    }

    addComment(resolve = false) {
        this.sendComment(this.newComment)
            .then(async () => {
                this.newComment = '';
                const osr = this.origSupportRequest;
                if (resolve && osr && osr.status !== SupportRequestStatus.Resolved) {
                    this.supportRequest!.status = SupportRequestStatus.Resolved;
                    (await firstValueFrom(this.dialog)).submit();
                }
            })
            .catch(ex => logger.error('addComment', ex));
    }

    async sendComment(message: string) {
        if (this.savingComment.value) {
            throw new Error('Unable to send comment, still sending the last one');
        }
        try {
            this.savingComment.next(true);
            assertNotNullish(this.supportRequest);
            const { email, id } = this.supportRequest;
            const accessLevel = this.clientState.authToken.value?.accessLevel;
            let adminName = '';
            if (accessLevel !== AccessLevel.Player) {
                adminName = accessLevel === AccessLevel.Admin ? 'Admin' : 'AdminHelper';
            }
            const author = adminName || email;
            const commentBody: SupportRequestComment = {
                id: '',
                created: '',
                message: message,
                author
            };
            const url = `/api/support-requests/${id}/comments`;
            const comment = await firstValueFrom(
                this.httpClient.post<SupportRequestComment>(url, commentBody)
            );
            this.comments.push(comment);
            this.supportRequestNotification.skipSupportRequestNotification({
                id: '',
                commentId: comment.id,
                updated: comment.created
            });
            this.savingComment.next(false);
        } catch (ex: unknown | HttpErrorResponse) {
            this.notifyError(ex);
        }
    }

    notifyError(ex: unknown) {
        let message = '';
        if (ex instanceof HttpErrorResponse) {
            message = ex.error?.message || ex.message;
        } else {
            assertIsError(ex);
            message = ex.message;
        }
        this.notify.error(message);
        logger.error(ex);
    }

    async getSupportRequest(id: string) {
        const url = `/api/support-requests/${id}`;
        const [supportRequest, comments] = await Promise.all([
            firstValueFrom(this.httpClient.get<SupportRequest>(url)),
            firstValueFrom(
                this.httpClient.get<SupportRequestComment[]>(`/api/support-requests/${id}/comments`)
            )
        ]);

        this.supportRequest = supportRequest;
        this.origSupportRequest = { ...supportRequest };
        this.wasResolved = [SupportRequestStatus.Resolved, SupportRequestStatus.WontFix].includes(
            supportRequest.status
        );
        this.comments = comments;
        this.changeDetectorRef.markForCheck();
        const params = parseParams(this.activatedRoute.snapshot.fragment);
        const addCommentParam = params.get(ParamKey.AddComment);
        if (typeof addCommentParam === 'string') {
            this.scrollToAddComment();
        }
    }

    async fillClientDetails() {
        const url = '/api/support-requests/client-info';
        const cb = '```';
        let clientDetails: SupportRequestClientInfo | null = null;
        try {
            clientDetails = await firstValueFrom(
                this.httpClient.get<SupportRequestClientInfo>(url)
            );
        } catch (ex) {
            if (ex instanceof HttpErrorResponse && ex.status == HttpStatusCode.NotFound) {
                this.foldSections = [];
            } else {
                throw ex;
            }
        }
        if (this.supportRequest) {
            this.supportRequest.description = DEFAULT_DESCRIPTION.replace(
                /{{([.A-Z_a-z]+)(\|)?}}/g,
                (_, expr, noDefault) => {
                    const sectionHeader =
                        (
                            {
                                clientTrackingState: '# Client Info',
                                userInfo: '# User Info'
                            } as Record<string, string>
                        )[expr] || '';
                    const value = evalPathExpr(expr, clientDetails || {});
                    if (value) {
                        if (typeof value === 'object') {
                            return `${sectionHeader}\n${cb}YAML\n${YamlStringify(value)}${cb}\n`;
                        }
                        return `${sectionHeader}${value}`;
                    }
                    return noDefault ? '' : '<!--- please enter a value --->';
                }
            );
        }
    }

    async submit(event: DialogEvent) {
        event.preventParentSlideSubmission();
        this.saving.next(true);
        try {
            if (this.newComment) {
                await this.sendComment(this.newComment);
                this.newComment = '';
            }
            assertNotNullish(this.supportRequest);
            if (this.supportRequest.id) {
                if (this.form.value?.dirty) {
                    const { id } = this.supportRequest;
                    const responsePromise = firstValueFrom(
                        this.httpClient.put<SupportRequest>(
                            `/api/support-requests/${id}`,
                            this.supportRequest
                        )
                    );
                    event.resolve(responsePromise.then(() => {}));
                    const response = await responsePromise;
                    this.supportRequestNotification.skipSupportRequestNotification({
                        id: response.id,
                        updated: response.updated
                    });
                } else {
                    event.resolve(Promise.resolve());
                }
            } else {
                const responsePromise = firstValueFrom(
                    this.httpClient.post<SupportRequest>(
                        '/api/support-requests',
                        this.supportRequest
                    )
                );
                event.resolve(responsePromise.then(() => {}));
                const response = await responsePromise;
                this.supportRequestNotification.skipSupportRequestNotification({
                    id: response.id,
                    updated: response.updated
                });
                const isPlayer =
                    this.clientState.authToken.value?.accessLevel === AccessLevel.Player;
                const url = isPlayer
                    ? fullClientRoutes.supportRequestList
                    : fullClientRoutes.adminSupportRequestList;
                await this.router.navigate([url], {
                    fragment: `${ParamKey.RequestId}=${response.id}`
                });
            }
            Object.values(this.form.value?.controls || {}).forEach(c => c.markAsPristine());
        } catch (ex: unknown) {
            this.notifyError(ex);
        } finally {
            this.saving.next(false);
        }
    }
}
